/**
 * One place that knows how a `processing_status` reads to a person.
 *
 * There are three surfaces that render this value — the badge, the live-detail
 * chip and the feed pill — and before this they each decided independently.
 * Adding `redaction_blocked` to the badge alone left the chip showing
 * `REDACTION_BLOCKED` and the pill showing nothing at all, which is how a status
 * ends up meaning three different things on three screens. Add a state here and
 * every surface picks it up.
 */
import { ProcessingStatus } from '../types/signals-live';

/** How each stored status reads. Anything absent falls back to title-casing. */
const LABELS: Record<ProcessingStatus, string> = {
  queued: 'Queued',
  pending: 'Queued',
  classifying: 'Classifying',
  summarising: 'Summarising',
  evaluating_ombudsman: 'Evaluating FOS',
  classified: 'Classified',
  processed: 'Processed',
  failed: 'Failed',
  escalated: 'Escalated',
  // Redaction could not run, so the signal never reached the model. The sweeper
  // returns it to the queue once the cause is fixed, so to a reader it is
  // genuinely still queued — not an error, and not something to act on.
  redaction_blocked: 'Queued',
  // Internal bookkeeping: the sweep parked this row and will re-claim it.
  // There is nothing for a reader to act on, so it reads like any other
  // waiting signal rather than leaking the sweep's states onto the screen.
  classification_requeued: 'Queued',
  // An imported record waiting for the drain to queue it at a fixed rate.
  // Work is owed and nothing is wrong, so to a reader it is simply queued.
  import_pending: 'Queued',
  // Nothing ever attempted this signal and it has aged past the recovery
  // window, so no sweep will. Deliberately not 'Failed': nothing failed, and a
  // destructive badge would send someone looking for an error that never
  // happened. Absent from IN_PROGRESS below, because no work is owed.
  abandoned: 'Abandoned',
};

/**
 * States where work is still owed on the signal, so a progress surface should
 * show it rather than hide it. `redaction_blocked` belongs here: it is waiting
 * on the recovery sweep, not finished.
 */
const IN_PROGRESS: ReadonlySet<string> = new Set<string>([
  'queued',
  'pending',
  'classifying',
  'summarising',
  'evaluating_ombudsman',
  'redaction_blocked',
  'classification_requeued',
  'import_pending',
  // Written mid-run by the pipeline, outside the union.
  'detecting_complaints',
]);

/**
 * Stages that mean no further work is owed.
 *
 * Kept separate from `IN_PROGRESS` because the records surfaces ask this of
 * `_progress.stage`, a free-form string off the realtime channel that carries
 * values this module has never heard of (`reclassifying`, and whatever the
 * worker publishes). They therefore ask what is finished and keep the spinner
 * on anything unrecognised; an allow-list would drop it mid-run.
 */
const TERMINAL_STAGES: ReadonlySet<string> = new Set<string>([
  'processed',
  'classified',
  'done',
  'failed',
  // Nothing ever attempted this signal and no sweep will, so a spinner here
  // promises work that is never coming.
  'abandoned',
]);

/** True when a stage or status means nothing further is owed. */
export function isTerminalStage(stage?: string | null): boolean {
  return !!stage && TERMINAL_STAGES.has(String(stage).toLowerCase());
}

/** Title-case an unknown value rather than showing a raw enum string. */
function titleCase(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** The human label for a stored status. Safe for unknown/missing values. */
export function processingStatusLabel(status?: string | null): string {
  if (!status) return '';
  const known = LABELS[status as ProcessingStatus];
  return known ?? titleCase(status);
}

/** True while the signal still has work owed on it. */
export function isProcessingStatusInProgress(status?: string | null): boolean {
  return !!status && IN_PROGRESS.has(status);
}

/**
 * True while a record still has work owed. A live progress stage is free-form, so
 * anything not terminal counts; before any event arrives the stored status
 * answers, from `IN_PROGRESS`. Both records views ask this.
 */
export function isRecordProcessing(record: {
  _progress?: { stage?: string } | null;
  processing_status?: string | null;
}): boolean {
  const live = record._progress?.stage;
  if (live) return !isTerminalStage(live);
  return isProcessingStatusInProgress(record.processing_status);
}
